import moment from "moment";
import { useEffect, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { SubmitButton } from "../components/btns";
import { ErrorLoading, PageLoading } from "../components/loading";
import { getPublicFeedback, submitPublicFeedback } from "../resources/api/events";
import PublicEventLayout from "./public-event-layout";

const bool = value => value === true || value === 1 || value === "1";

const Rating = ({ name, value, onChange }) => (
    <div className="d-flex flex-wrap gap-2" role="radiogroup" aria-label="Rating from 1 to 5">
        <input type="hidden" name={name} value={value || ""} />
        {[1, 2, 3, 4, 5].map(number => (
            <Button key={number} type="button" variant={String(value) === String(number) ? "success" : "outline-secondary"} className="rating-button" aria-pressed={String(value) === String(number)} onClick={() => onChange(String(number))}>
                <i className="fas fa-star me-1" />{number}
            </Button>
        ))}
    </div>
);

const Options = ({ question, value, onChange }) => {
    const selected = value?.option_id || (value?.is_other ? "other" : "");
    return (
        <div>
            {(question.options || []).map(option => (
                <Form.Check
                    key={option.id}
                    type="radio"
                    id={`question-${question.id}-option-${option.id}`}
                    name={`question-${question.id}`}
                    label={option.label}
                    checked={selected === option.id}
                    onChange={() => onChange({ option_id: option.id, response: option.label, is_other: false })}
                    className="mb-2"
                />
            ))}
            {bool(question.allow_other) && <>
                <Form.Check
                    type="radio"
                    id={`question-${question.id}-other`}
                    name={`question-${question.id}`}
                    label="Other"
                    checked={selected === "other"}
                    onChange={() => onChange({ option_id: null, response: "", is_other: true })}
                    className="mb-2"
                />
                {selected === "other" && <Form.Control className="mt-2" maxLength={5000} value={value.response || ""} onChange={e => onChange({ ...value, response: e.currentTarget.value })} placeholder="Please specify" autoFocus />}
            </>}
        </div>
    );
};

const EventFeedback = () => {
    const { shortCode } = useParams();
    const [event, setEvent] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [responses, setResponses] = useState({});
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [complete, setComplete] = useState(false);

    useEffect(() => {
        getPublicFeedback(shortCode)
            .then(data => {
                const receivedEvent = data.event || {};
                setEvent({ ...receivedEvent, start_date: receivedEvent.start_date ? moment(receivedEvent.start_date).format("DD MMM YYYY") : "" });
                setQuestions(data.questions || receivedEvent.feedback_questions || []);
            })
            .catch(setError)
            .finally(() => setLoaded(true));
    }, [shortCode]);

    const submit = e => {
        e.preventDefault();
        const responseValue = response => typeof response === "object" ? response?.response : response;
        const missing = questions.some(question => !String(responseValue(responses[question.id]) || "").trim());
        if (missing) return setError("Please answer every feedback question before submitting.");
        setSubmitting(true);
        setError(null);
        submitPublicFeedback(shortCode, questions.map(question => {
            const answer = responses[question.id];
            return typeof answer === "object"
                ? { question_id: question.id, option_id: answer.option_id, response: answer.response }
                : { question_id: question.id, response: answer };
        }))
            .then(() => setComplete(true))
            .catch(setError)
            .finally(() => setSubmitting(false));
    };

    if (!loaded) return <PageLoading>Loading feedback form...</PageLoading>;
    if (error && !event) return <ErrorLoading>{error}</ErrorLoading>;

    return (
        <PublicEventLayout event={event}>
            {complete ? (
                <div className="text-center py-4">
                    <i className="fas fa-heart fa-4x text-success mb-3" />
                    <h2 className="h4">Thank you for your feedback</h2>
                    <p className="text-muted mb-0">Your anonymous response has been received.</p>
                </div>
            ) : !bool(event?.feedbackOpen) ? (
                <Alert variant="secondary" className="mb-0 text-center">Feedback for this event is currently closed.</Alert>
            ) : questions.length === 0 ? (
                <Alert variant="secondary" className="mb-0 text-center">No feedback questions are available.</Alert>
            ) : (
                <>
                    <h2 className="h5 mb-1">Share your feedback</h2>
                    <p className="text-muted mb-4"><i className="fas fa-user-shield me-1" />Your feedback is anonymous and is not linked to your registration.</p>
                    {error && <Alert variant="danger">{error}</Alert>}
                    <Form onSubmit={submit}>
                        {questions.map((question, index) => (
                            <Form.Group key={question.id} className="public-question mb-4 pb-4">
                                <input type="hidden" name={`responses[${index}][question_id]`} value={question.id} />
                                <Form.Label className="fw-bold d-block mb-3">{index + 1}. {question.question}</Form.Label>
                                {question.question_type === "rating" ? (
                                    <Rating name={`responses[${index}][response]`} value={responses[question.id]} onChange={value => setResponses(current => ({ ...current, [question.id]: value }))} />
                                ) : question.question_type === "options" ? (
                                    <Options question={question} value={responses[question.id]} onChange={value => setResponses(current => ({ ...current, [question.id]: value }))} />
                                ) : (
                                    <Form.Control as="textarea" rows={4} name={`responses[${index}][response]`} maxLength={5000} value={responses[question.id] || ""} onChange={e => {
                                        const value = e.currentTarget.value;
                                        setResponses(current => ({ ...current, [question.id]: value }));
                                    }} placeholder="Type your response" />
                                )}
                            </Form.Group>
                        ))}
                        <div className="text-end"><SubmitButton type="submit" isSubmitting={submitting}>Submit Feedback</SubmitButton></div>
                    </Form>
                </>
            )}
        </PublicEventLayout>
    );
};

export default EventFeedback;
